import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Menu, Table } from 'semantic-ui-react';
import { GlobalTableStyle } from 'components/atoms/tables/smarttables/CommonAssets';
import { formatColumnsForExport } from 'helpers/tables';
import GlobalSearch from 'components/atoms/tables/smarttables/tablePlugins/GlobalSearch';
import Pagination from 'components/atoms/tables/smarttables/tablePlugins/Pagination';
import Actions from 'components/atoms/tables/smarttables/tablePlugins/Actions';
import ExportData from 'components/atoms/tables/smarttables/tablePlugins/ExportData';
import GroupBy from 'components/atoms/tables/smarttables/tablePlugins/GroupBy';
import ColumnConfiguration from 'components/atoms/tables/smarttables/tablePlugins/ColumnConfiguration';
import ColumnFilters from 'components/atoms/tables/smarttables/tablePlugins/ColumnFilters';
import useTable from 'components/atoms/tables/smarttables/useTable';
import styled from 'styled-components';
import DataToggle from 'components/atoms/tables/smarttables/tablePlugins/DataToggle';
import Aggregate from 'components/atoms/tables/smarttables/tablePlugins/Aggregate';
import { addExportConfig, updateExportConfig, destroyExportConfig } from 'services/exports/duck';

const Container = styled.div`
    max-height: ${props => (props.$freeze ? '75vh' : 'none')};
    display: ${props => (props.$loading === true ? 'none' : 'inline')};
    width: 100%;
    overflow-x: auto;
    position: relative;
    min-width: 800px;
`;

const ActionBar = styled(Menu)`
    margin: 5px !important;
`;

const MenuItem = styled(Menu.Item)`
    padding: 0px !important;
    margin-bottom: auto !important;
`;

const MenuItemRight = styled(MenuItem)`
    margin-left: auto !important
`;

const Left = styled.div`
    margin-left: auto;
    margin-right: 50px;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const StyledTable = styled(Table)`

    &&& th {
        font-weight: bold;
        height: 24px;
        font-size: 12px;
        background-color: #E2E2E2;
        text-transform: uppercase;
        border-radius: 0;
    }
    
    &&& thead {
        border-radius: 0;

        & th { 
            justify-content: center;
            align-items: center;
            text-align:center;  
        }
    }
`;

const checkComponent = (comp, defaultComp) => {
    if (typeof comp === 'boolean' && comp) {
        return defaultComp;
    } else if (typeof comp === 'boolean') {
        return null;
    }
    return comp;
};

const flattenColumns = (columns) => {
    const flattenedColumns = [];
    const flatten = (array) => {
        array.forEach((element) => {
            flattenedColumns.push(element);
            if (element.columns) {
                flatten(element.columns);
            }
        });
    };
    flatten(columns);
    return flattenedColumns;
};

const DefaultTable = ({
    fileName,
    columns,
    data,
    title = null,
    exportData = false,
    globalSearch = true,
    pagination = true,
    freeze = false,
    columnConfiguration = true,
    actionsComponent = true,
    filterComponent = true,
    groupByComponent = true,
    globalSearchPlaceholder = '',
    setSearchFnc = null,
    controlledSearch = false,
    cacheKey = null,
    actions = [],
    defaultState = {},
    GroupWrapper = ({ children }) => (children),
    defaultFilters,
    alwaysShowActionBar,
    dataToggle,
    aggregate = false,
    exportName = null,
    addExport,
    updateExport,
    destroyExport
}) => {
    const defaultSorted = defaultState.sortBy || [];
    const pageSize = defaultState.pageSize || 20;
    const selectEnabled = !!actions.length;
    const isInitialMount = useRef(true);
    const [isGrouped, setIsGrouped] = useState(false);
    const [grouping, setGrouping] = useState([]);

    const hiddenColumns = flattenColumns(columns)
        .filter(x => x.hidden && !(typeof x.canHide === 'boolean' && !x.canHide))
        .map(x => x.id || x.accessor);

    const [tableObject, tableArray] = useTable({
        columns,
        data,
        pagination,
        globalSearch,
        freeze,
        globalSearchPlaceholder,
        setSearchFnc,
        controlledSearch,
        disableSortBy: isGrouped,
        enableSelection: selectEnabled,
        initialState: {
            hiddenColumns,
            selectedRows: {},
            groupBy: grouping,
            ...defaultState
        },
    });
    useEffect(() => {
        if (exportName) {
            const columnsFormatted = formatColumnsForExport(columns);
            if (isInitialMount.current) {
                isInitialMount.current = false;
                addExport({
                    exportName,
                    type: 'table',
                    title: title || exportName,
                    keyField: null,
                    minRows: data.length < pageSize ? data.length : pageSize,
                    exportRows: null,
                    data,
                    columns: columnsFormatted,
                    defaultSorted,
                    meta: null,
                    headerDepth: null
                });
            } else {
                updateExport({
                    exportName,
                    type: 'table',
                    title: title || exportName,
                    keyField: null,
                    minRows: data.length < pageSize ? data.length : pageSize,
                    exportRows: null,
                    data,
                    columns: columnsFormatted,
                    defaultSorted,
                    meta: null,
                    headerDepth: null
                });
            }
        }
        return () => {
            if (exportName) {
                destroyExport({ exportName });
            }
        };
    }, [exportName, addExport, updateExport, destroyExport]);

    useEffect(() => {
        setIsGrouped(!!tableObject.tableProps.state.groupBy.length);
    }, [tableObject.tableProps.state.groupBy]);

    const [isLoadingColumn, setIsLoadingColumn] = useState(typeof columnConfiguration === 'boolean' && !isGrouped ? columnConfiguration : false);
    const [isLoadingFilter, setIsLoadingFilter] = useState(typeof filterComponent === 'boolean' ? filterComponent : false);
    const groupByColumns = columns.filter(x => x.canGroup || x.entityGroup).map(x => x.accessor);

    return (
        <Container $loading={isLoadingColumn || isLoadingFilter} $freeze={freeze}>
            <GlobalTableStyle />
            <ActionBar stackable secondary>
                {checkComponent(
                    filterComponent,
                    <MenuItem>
                        <ColumnFilters
                            {...tableObject}
                            defaultFilters={defaultFilters}
                            cacheKey={cacheKey}
                            isReady={() => setIsLoadingFilter(false)}
                        />
                    </MenuItem>
                )}
                {dataToggle &&
                    <MenuItem>
                        <DataToggle
                            {...dataToggle}
                            {...tableObject}
                            cacheKey={cacheKey}
                        />
                    </MenuItem>
                }
                {!!groupByColumns.length && checkComponent(groupByComponent,
                    <MenuItem>
                        <Left>
                            <GroupBy
                                groupByColumns={groupByColumns}
                                tableObject={tableObject}
                                setGrouping={setGrouping}
                            />
                        </Left>
                    </MenuItem>
                )}
                <MenuItemRight>
                    {!isLoadingFilter && !isLoadingColumn &&
                        checkComponent(exportData, <ExportData fileName={fileName} {...tableObject}/>)
                    }
                    {checkComponent(columnConfiguration, <ColumnConfiguration
                        isReady={() => setIsLoadingColumn(false)}
                        cacheKey={cacheKey}
                        {...tableObject}
                    />)}
                </MenuItemRight>
            </ActionBar>
            <TableWrapper>
                {tableArray.map(({ id, groupByColumn, entity, TableView, tableRows }, index) =>
                    (
                        <GroupWrapper key={id || index} entity={entity} groupByColumn={groupByColumn} id={id}>
                            {!!actions.length && <Actions tableRows={tableRows}
                                groupId={id}
                                groupType={groupByColumn}
                                tableProps={tableObject.tableProps}
                                actions={actions}
                                alwaysShowActionBar={alwaysShowActionBar}
                            />}
                            <StyledTable unstackable sortable celled {...tableObject.tableProps.getTableProps()}>
                                {actionsComponent && null}
                                {checkComponent(globalSearch, <GlobalSearch {...tableObject}/>)}
                                <TableView/>
                                {(!!pagination && !isGrouped) &&
                                    <Table.Footer>
                                        <Table.Row>
                                            <Table.HeaderCell
                                                colSpan={tableObject.tableProps.visibleColumns.length}
                                            >
                                                {checkComponent(pagination, <Pagination {...tableObject}/>)}
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Footer>
                                }
                                { checkComponent((!isGrouped && aggregate), Aggregate(tableObject.tableProps)) }
                            </StyledTable>
                        </GroupWrapper>)
                )}
            </TableWrapper>
        </Container>
    );
};
export default connect(null, {
    addExport: addExportConfig,
    updateExport: updateExportConfig,
    destroyExport: destroyExportConfig
})(DefaultTable);



// WEBPACK FOOTER //
// ./src/components/atoms/tables/smarttables/DefaultTable.js